import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter, map, withLatestFrom } from 'rxjs/operators';

import {
  deleteDeviceFileStatus,
  getDeviceFileRequest,
  getDeviceFileStatus,
  setDeviceFileStatus,
} from './device-file.actions';
import { deviceRelatedParameterId } from './device-parameter.model';
import { getDeviceSerialNumberFromParameterId } from './parameter.model';
import {
  removeDevicePlantModelParameters,
  setPlantModelParameterValuesRequest,
  upsertPlantModelParameters,
} from './plant-model-parameter.actions';
import { plantModelTemplateParameters } from './plant-model-parameter.model';
import { toggleExpertMode } from './setting.actions';

import { AppState } from './index';

import { deleteDeviceFileCompleted, getDeviceFileEnded, setDeviceFileCompleted } from '@app/models/status-resolve';
import { DeviceService } from '@app/services/device.service';
import { PlantModelParameterService } from '@app/services/plant-model-parameter.service';
import { getCurrentValue } from '@app/util';

@Injectable()
export class PlantModelParameterEffects {
  private actions$ = inject(Actions);
  private deviceService = inject(DeviceService);
  private plantModelParameterService = inject(PlantModelParameterService);
  private store$ = inject<Store<AppState>>(Store);

  upsertPlantModelParametersOnGetDeviceFilePlantModelStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getDeviceFileStatus),
      filter((action) => getDeviceFileEnded(action.deviceFile) && action.deviceFile.name === 'plant_model.csv'),
      map((action) => {
        const {
          device,
          deviceFile: { content },
        } = action;

        const inertiaId = deviceRelatedParameterId(device, plantModelTemplateParameters[0]);
        const frictionId = deviceRelatedParameterId(device, plantModelTemplateParameters[1]);

        const parameters = [
          {
            ...plantModelTemplateParameters[0],
            deviceSerialNumber: device.serialNumber,
            id: inertiaId,
            value: 0,
          },
          {
            ...plantModelTemplateParameters[1],
            deviceSerialNumber: device.serialNumber,
            id: frictionId,
            value: 0,
          },
        ];

        if (content) {
          let plantModelParameters = getCurrentValue(
            this.plantModelParameterService.selectDevicePlantModelParameters(device),
          );

          if (!plantModelParameters?.length) {
            // @ts-ignore
            plantModelParameters = parameters;
          }

          // Use values from the plant_model.csv file.

          const rows = content.split('\n');
          const [frictionValue, inertiaValue] = rows[1].split(',').reverse();

          const inertiaParameter = plantModelParameters.find((p) => p.id === inertiaId);
          const frictionParameter = plantModelParameters.find((p) => p.id === frictionId);

          plantModelParameters = [
            { ...inertiaParameter, floatValue: parseFloat(inertiaValue), value: parseFloat(inertiaValue) },
            { ...frictionParameter, floatValue: parseFloat(frictionValue), value: parseFloat(frictionValue) },
          ];

          return upsertPlantModelParameters({ plantModelParameters });
        } else {
          // @ts-ignore
          return upsertPlantModelParameters({ plantModelParameters: parameters });
        }
      }),
    ),
  );

  handleSetPlantModelParameterValuesRequest$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(setPlantModelParameterValuesRequest),
        map((action) => {
          const { plantModelParameterValues: values, suppressToastr, timeout } = action;

          const keys = Object.keys(values);
          const serialNumber = getDeviceSerialNumberFromParameterId(keys[0]);
          const device = getCurrentValue(this.deviceService.selectDeviceByDeviceSerialNumber(serialNumber));
          const plantModelFile = getCurrentValue(
            this.deviceService.selectDeviceFile({ serialNumber }, 'plant_model.csv'),
          );

          let plantModel = plantModelFile?.content;
          if (!plantModel) {
            plantModel = '1\n0,0';
          }
          const rows = plantModel.split('\n');

          let [frictionValue, inertiaValue] = rows[1].split(',').reverse();

          keys.forEach((key) => {
            if (key.startsWith(plantModelTemplateParameters[0].fieldName)) {
              inertiaValue = values[key];
            } else if (key.startsWith(plantModelTemplateParameters[1].fieldName)) {
              frictionValue = values[key];
            }
          });

          const row2data = rows[1].split(',');
          row2data[row2data.length - 2] = parseFloat(inertiaValue);
          row2data[row2data.length - 1] = parseFloat(frictionValue);

          const content = new TextEncoder().encode(`${rows[0]}\n${row2data.join(',')}\n`);

          this.deviceService.requestSetDeviceFile(device, 'plant_model.csv', content, false, suppressToastr, timeout);
        }),
      ),
    { dispatch: false },
  );

  handleSetDeviceFilePlantModelStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setDeviceFileStatus),
      filter((action) => setDeviceFileCompleted(action.deviceFile) && action.deviceFile.name === 'plant_model.csv'),
      map((action) =>
        getDeviceFileRequest({
          device: action.device,
          filename: 'plant_model.csv',
          loadIntoStore: true,
          decodeText: true,
          suppressToastr: { begin: true, success: true },
          timeout: 5000,
        }),
      ),
    ),
  );

  removePlantModelParametersOnDeleteDeviceFilePlantModelStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteDeviceFileStatus),
      filter((action) => deleteDeviceFileCompleted(action.deviceFile) && action.deviceFile.name === 'plant_model.csv'),
      map((action) => removeDevicePlantModelParameters({ device: action.device })),
    ),
  );

  updatePlantModelParametersWriteAccessOnToggleExpertMode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(toggleExpertMode),
      withLatestFrom(this.store$),
      map(([_, state]) => {
        const { expertMode: expertMode } = state.setting;
        const plantModelParameters = Object.values(state.plantModelParameters.entities).map((p) => {
          p.writeAccess = expertMode;
          return p;
        });
        return upsertPlantModelParameters({ plantModelParameters });
      }),
    ),
  );
}
